import { groupedLayers } from './layer_order.js';
import { MIN_SCALE, MAX_SCALE } from './constants.js';

const MOBILE_QUERY = '(max-width: 767.98px), (pointer: coarse) and (max-width: 1023.98px)';

export const MobileEditorMethods = {
  _connectMobileEditor() {
    if (this.workspaceValue) return this._connectWorkspace();
    if (this.readonlyValue || !this.hasEditorTarget) return;
    this._mobileMedia = window.matchMedia(MOBILE_QUERY);
    this._mobileViewportChanged = () => {
      if (!this.hasEditorTarget) return;
      const changed = this._mobileEditor !== this._mobileMedia.matches;
      this._mobileEditor = this._mobileMedia.matches;
      const editor = this.editorTarget;
      editor.classList.toggle('game-map-editor--mobile', this._mobileEditor);
      const visual = window.visualViewport;
      const bounds = editor.getBoundingClientRect();
      editor.style.setProperty('--map-mobile-width', `${bounds.width}px`);
      editor.style.setProperty('--map-mobile-left', `${editor.closest('dialog') ? 0 : bounds.left}px`);
      editor.style.setProperty(
        '--map-visible-height',
        `${Math.max(0, (visual?.height || window.innerHeight) + (visual?.offsetTop || 0) - Math.max(bounds.top, visual?.offsetTop || 0))}px`
      );
      if (changed) {
        if (this._stage) this._restoreToolbarVisibility();
        this._syncFogBrushPopover();
        this._refreshShapePopover();
        this._syncMapTitle();
        this._syncEffectRotationHandle();
      }
      this._syncMobileControls();
    };
    this._mobileMedia.addEventListener('change', this._mobileViewportChanged);
    this._mobileControlsObserver = new ResizeObserver(() => {
      if (!this.hasEditorTarget || !this.hasMobileControlsTarget) return;
      this.editorTarget.style.setProperty('--map-controls-height', `${this.mobileControlsTarget.offsetHeight}px`);
      this.editorTarget.style.setProperty('--map-zoom-height', `${this.mobileZoomControlsTarget.offsetHeight}px`);
      if (this.hasShapePopoverTarget)
        this.shapePopoverTarget.dispatchEvent(new CustomEvent('toolbar-popover:reposition'));
    });
    this._mobileControlsObserver.observe(this.mobileControlsTarget);
    this._mobileControlsObserver.observe(this.mobileZoomControlsTarget);
    window.addEventListener('resize', this._mobileViewportChanged);
    window.visualViewport?.addEventListener('resize', this._mobileViewportChanged);
    window.visualViewport?.addEventListener('scroll', this._mobileViewportChanged);
    this._rememberOnPageHide = () => {
      this._cancelCanvasGesture();
      this._rememberEditorState();
    };
    window.addEventListener('pagehide', this._rememberOnPageHide);
    this._mobileViewportChanged();
  },

  _disconnectMobileEditor() {
    if (this.workspaceValue) return this._disconnectWorkspace();
    this._mobileControlsObserver?.disconnect();
    this._mobileMedia?.removeEventListener('change', this._mobileViewportChanged);
    window.removeEventListener('resize', this._mobileViewportChanged);
    window.visualViewport?.removeEventListener('resize', this._mobileViewportChanged);
    window.visualViewport?.removeEventListener('scroll', this._mobileViewportChanged);
    window.removeEventListener('pagehide', this._rememberOnPageHide);
  },

  exploreMap(event) {
    event?.preventDefault();
    this._cancelCanvasGesture();
    this._storeActiveToolbar(null);
    this._collapseMobilePanels();
    this._applyTool('drag');
  },

  mobileToolbar(event) {
    event.preventDefault();
    const toolbar = event.currentTarget.dataset.konvaMapToolbarButton;
    if (this.playerValue && toolbar !== 'shapes') return;
    if (toolbar === 'settings') {
      if (this._mobilePanel === toolbar) this.collapseMapControls();
      else this._showToolbar(toolbar);
      return;
    }
    this._cancelCanvasGesture();
    this.collapseMapControls();
    this._activateToolbar(toolbar, toolbar === 'shapes' ? 'select' : null);
    this._rememberEditorState();
  },

  mobileShapes(event) {
    event.preventDefault();
    const panel = event.currentTarget.dataset.shapesPanel;
    if (this._mobilePanel === 'shapes' && this._mobileShapesPanel === panel) return this.collapseMapControls();
    this._mobileShapesPanel = panel;
    this._showToolbar('shapes', 'select');
    this._rememberEditorState();
  },

  mobilePositionFields(event) {
    event.preventDefault();
    if (this.playerValue) return;
    if (this._mobilePanel === 'position') this.collapseMapControls();
    else this._showToolbar('position');
  },

  mobileZoom(event) {
    event.preventDefault();
    if (!this._stage) return;
    const action = event.currentTarget.dataset.zoomAction;
    const preset = Number(event.currentTarget.dataset.zoom);
    if (this._positionManagerActive) {
      if (action === 'fit') return this.resetPosition();
      const zoom = action ? this.positionZoomValue + (action === 'in' ? 5 : -5) : (preset - 1) * 100;
      this.positionZoomValue = Math.max((MIN_SCALE - 1) * 100, Math.min((MAX_SCALE - 1) * 100, zoom));
      this._savePosition();
    } else if (action === 'fit') this._resetView();
    else this._applyZoom(action ? this._currentScale * (action === 'in' ? 1.25 : 0.8) : preset);
  },

  collapseMapControls(event) {
    event?.preventDefault();
    if (this.workspaceValue) return;
    this._shapePopoverCollapsed = true;
    this._hideShapePopover();
    this._collapseMobilePanels();
    this._rememberEditorState();
  },

  _collapseMobilePanels() {
    for (const name of ['settings', 'position', 'fog', 'shapes']) {
      this._toolbarElement(name)?.classList.remove('show', 'collapsing');
    }
    this._mobilePanel = null;
    this._syncFogBrushPopover();
    this._syncMobileControls();
  },

  editSelectedShape(event) {
    event?.preventDefault();
    if (!this._selectedShapeId) return;
    if (this.hasShapePopoverTarget && this.shapePopoverTarget.dataset.toolbarPopoverOpenValue === 'true')
      return this.collapseMapControls();
    this._applyTool('select');
    this._selectShape(this._selectedShapeId);
  },

  _openMobileLayerPanel() {
    if (this.workspaceValue) return;
    if (!this._mobileEditor) return;
    this._collapseMobilePanels();
    this._mobilePanel = 'layer';
    this._syncMobileControls();
  },

  _syncMobileControls() {
    if (this.workspaceValue) return this._syncWorkspaceControls();
    if (!this.hasMobileControlsTarget) return;
    const placing = this._tool === 'area-effect' && !!this._pendingAreaEffect;
    const placingCharacter = this._tool === 'character' && !!this._pendingCharacterToken;
    const toolbar =
      this._mobilePanel === 'settings'
        ? 'settings'
        : placing || placingCharacter
          ? 'shapes'
          : this._toolbarForTool(this._tool);
    const exploring = this._tool === 'drag' && toolbar !== 'settings';
    this.mobileExploreTarget.classList.toggle('active', exploring);
    this.mobileExploreTarget.setAttribute('aria-pressed', String(exploring));
    if (this._mobileEditor) this._setToolbarTriggerActive(this.playerValue ? 'shapes' : toolbar);
    for (const group of this.mobileToolsTargets) {
      const active = group.dataset.toolbar === (this.playerValue ? 'shapes' : toolbar);
      group.hidden = !active || placing || placingCharacter;
      if (group.dataset.toolbar === 'position') {
        const button = group.querySelector('button');
        const expanded = this._mobilePanel === 'position';
        button.classList.toggle('active', expanded);
        button.setAttribute('aria-expanded', String(expanded));
      }
    }
    for (const button of this.mobileShapeActionTargets) {
      const active = this._mobilePanel === 'shapes' && this._mobileShapesPanel === button.dataset.shapesPanel;
      button.classList.toggle('active', active);
      button.setAttribute('aria-expanded', String(active));
    }
    if (this.hasMobileBrushTarget) this.mobileBrushTarget.hidden = !['draw', 'erase'].includes(this._tool);
    this.mobileInstructionTarget.textContent = placing
      ? 'Touch or drag to preview, then tap Place.'
      : placingCharacter
        ? 'Tap a square to place your character. Two fingers navigate.'
        : this._tool === 'shape-rect'
          ? 'Drag to draw. Two fingers navigate.'
          : this._positionManagerActive
            ? 'Drag or pinch to change the players’ view.'
            : '';
    this.mobileInstructionTarget.classList.toggle('text-warning', placing || this._tool === 'shape-rect');
    this.placeEffectTarget.hidden = !placing;
    this.placeEffectTarget.disabled = !this._areaEffectPlacementPoint;
    this.cancelEditTarget.hidden = !placing && !placingCharacter && !this._gestureSnapshot;
    const editing = this.hasShapePopoverTarget && this.shapePopoverTarget.dataset.toolbarPopoverOpenValue === 'true';
    for (const button of this.editSelectedTargets) {
      button.disabled = !this._selectedShapeId;
      button.classList.toggle('active', !!editing);
      button.setAttribute('aria-expanded', String(!!editing));
    }
    this.mobileGridNoticeTarget.hidden = !placing || this.gridEnabledValue;
    this.editorTarget.dataset.mobilePanel = this._mobilePanel || '';
    this.editorTarget.dataset.mobileShapesPanel = this._mobileShapesPanel || 'add';
    this._syncSaveStatus?.();
  },

  cancelMapEdit(event) {
    event?.preventDefault();
    this._cancelCanvasGesture();
    if (this._pendingAreaEffect || this._pendingCharacterToken) this._applyTool('select');
    this._syncMobileControls();
  },

  placeTouchEffect(event) {
    event?.preventDefault();
    if (this._areaEffectPlacementPoint && !this._touchGesture) this._placeAreaEffect(this._areaEffectPlacementPoint);
  },

  moveLayer(event) {
    event.preventDefault();
    event.stopPropagation();
    const shapes = groupedLayers(this._editableShapes())
      .reverse()
      .map((group) => group.shape);
    const oldIndex = shapes.findIndex((shape) => shape.id === event.currentTarget.dataset.shapeId);
    const newIndex = oldIndex + Number(event.currentTarget.dataset.direction);
    if (oldIndex < 0 || newIndex < 0 || newIndex >= shapes.length) return;
    const id = shapes[oldIndex].id;
    this._onShapeReorder({ oldIndex, newIndex });
    this._updateShapesList();
    this.shapesListTarget
      .querySelector(`[data-shape-id="${CSS.escape(id)}"] [data-direction="${event.currentTarget.dataset.direction}"]`)
      ?.focus();
  },

  _rememberEditorState() {
    if (this.readonlyValue || !this._stage || !this.hasSaveUrlValue || this.element.dataset.konvaMapReady !== 'true')
      return;
    const view = this._editorViewBeforePosition || this._canvasView();
    const state = {
      image: this.reinitializeValue,
      view,
      width: view.width || this._stage.width(),
      height: view.height || this._stage.height(),
      selected: this._selectedShapeId,
      collapsed: this._shapePopoverCollapsed,
      tool: ['area-effect', 'character'].includes(this._tool) ? 'select' : this._tool,
      toolbar: this._preferredActiveToolbar(),
      panel: this._mobilePanel,
      shapesPanel: this._mobileShapesPanel
    };
    try {
      window.sessionStorage.setItem(this._toolbarStorageKey('view'), JSON.stringify(state));
    } catch {
      /* Storage may be disabled. */
    }
  },

  _restoreEditorState() {
    if (this.readonlyValue || !this._stage) return;
    let state;
    try {
      state = JSON.parse(window.sessionStorage.getItem(this._toolbarStorageKey('view')));
    } catch {
      return;
    }
    if (!state || state.image !== this.reinitializeValue) return;
    const sameToolbar = state.toolbar === this._preferredActiveToolbar();
    const view = state.view;
    if (!view || ![view.x, view.y, view.scale, state.width, state.height].every(Number.isFinite) || view.scale <= 0)
      return;
    const restored = {
      ...view,
      x: view.x + (this._stage.width() - state.width) / 2,
      y: view.y + (this._stage.height() - state.height) / 2,
      width: this._stage.width(),
      height: this._stage.height()
    };
    if (this._positionManagerActive) this._editorViewBeforePosition = restored;
    else {
      this._currentScale = view.scale;
      this._stage.scale({ x: view.scale, y: view.scale });
      this._stage.position(restored);
      if (sameToolbar && state.tool === 'select' && this._findShapeById(state.selected)) {
        this._applyTool('select');
        this._selectShape(state.selected);
      }
      this._updateZoomDisplay();
    }
    if (this._mobileEditor && sameToolbar) {
      this._mobilePanel = state.panel;
      this._mobileShapesPanel = state.shapesPanel;
      this._shapePopoverCollapsed = state.collapsed;
      this._restoreToolbarVisibility();
    }
  }
};
